import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  Grid,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  Box,
  Alert,
  Snackbar,
  MenuItem,
  InputLabel,
  Select
} from '@mui/material';
import axios from 'axios';
import { format } from 'date-fns';
import { useAuth } from '../context/AuthContext';
import { getCurrentOrganization } from '../utils/organizationContext';
import FormOrganizationBanner from './FormOrganizationBanner';
import countries from '../data/countries';

const FoundationSchoolForm = () => {
  const navigate = useNavigate();
  const { organizationSlug: urlSlug } = useParams();
  const { organizationSlug } = useAuth();
  
  // Get today's date in YYYY-MM-DD format
  const today = new Date();
  const localDate = format(today, 'yyyy-MM-dd');
  
  const [formData, setFormData] = useState({
    enrollment_date: localDate,
    first_name: '',
    last_name: '',
    street_address: '',
    city: '',
    state: '',
    postal_code: '',
    country: 'Canada',
    phone_number: '',
    email: '',
    date_of_birth: '',
    gender: '',
    marital_status: '',
    occupation: '',
    reason_for_enrollment: '',
    expectations: '',
    baptized_in_holy_ghost: '',
    class_taken: '',
    feedback: '',
    status: 'pending'
  });
  
  const [showSuccess, setShowSuccess] = useState(false);
  const [error, setError] = useState('');
  const [isCheckingDuplicate, setIsCheckingDuplicate] = useState(false);
  const [duplicateInfo, setDuplicateInfo] = useState(null);
  const [currentOrgSlug, setCurrentOrgSlug] = useState(null);

  // Get organization from URL
  useEffect(() => {
    const slug = urlSlug || organizationSlug || getCurrentOrganization();
    setCurrentOrgSlug(slug);
    
    if (!slug && !organizationSlug && window.location.pathname === '/foundation-school') {
      window.location.href = '/';
    }
  }, [urlSlug, organizationSlug]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Function to check for duplicates
  const checkForDuplicate = async (data) => {
    try {
      setIsCheckingDuplicate(true);
      setDuplicateInfo(null);
      
      const checkData = {
        email: data.email || '',
        phone_number: data.phone_number || '',
        enrollment_date: data.enrollment_date,
        ...(currentOrgSlug && { organization_slug: currentOrgSlug })
      };

      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:3001'}/api/foundation-school/check-duplicate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(checkData),
      });

      if (response.ok) {
        const result = await response.json();
        if (result.isDuplicate) {
          setDuplicateInfo(result.duplicate);
          return true;
        }
      }
      return false;
    } catch (error) {
      console.error('Error checking for duplicate:', error);
      return false;
    } finally {
      setIsCheckingDuplicate(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setError('');
      setDuplicateInfo(null);
      
      // First check for duplicates
      const isDuplicate = await checkForDuplicate(formData);
      if (isDuplicate) {
        return;
      }

      const response = await axios.post(
        `${process.env.REACT_APP_API_URL || 'http://localhost:3001'}/api/foundation-school`,
        {
          ...formData,
          ...(currentOrgSlug && { organization_slug: currentOrgSlug })
        }
      );

      if (response.status === 201) {
        setShowSuccess(true);
        setTimeout(() => {
          const successPath = currentOrgSlug ? `/${currentOrgSlug}/foundation-school-success` : '/foundation-school-success';
          navigate(successPath);
        }, 2000);
      }
    } catch (err) {
      if (err.response?.status === 409) {
        setDuplicateInfo(err.response.data.duplicate);
        setError(err.response.data.message || 'You have already enrolled for this date.');
        return;
      }
      
      setError(err.response?.data?.error || 'Failed to submit form');
    }
  };

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Paper elevation={3} sx={{ p: 4 }}>
        <Typography variant="h4" gutterBottom align="center" sx={{ color: 'primary.main', mb: 1 }}>
          Foundation School Enrollment
        </Typography>
        <FormOrganizationBanner subtitle="Foundation school enrollment for this church branch" />
        <Typography 
          variant="body2" 
          align="center" 
          sx={{ 
            mb: 4, 
            fontStyle: 'italic',
            color: 'text.secondary',
            maxWidth: '600px',
            mx: 'auto'
          }}
        >
          "Study to shew thyself approved unto God, a workman that needeth not to be ashamed, rightly dividing the word of truth." - 2 Timothy 2:15 (KJV)
        </Typography>

        <form onSubmit={handleSubmit}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                type="date"
                name="enrollment_date"
                label="Enrollment Date"
                value={formData.enrollment_date}
                onChange={handleChange}
                InputLabelProps={{ shrink: true }}
                required
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                name="first_name"
                label="First Name"
                value={formData.first_name}
                onChange={handleChange}
                required
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                name="last_name"
                label="Last Name"
                value={formData.last_name}
                onChange={handleChange}
                required
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                name="street_address"
                label="Street Address"
                value={formData.street_address}
                onChange={handleChange}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                name="city"
                label="City"
                value={formData.city}
                onChange={handleChange}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                name="state"
                label="State/Province"
                value={formData.state}
                onChange={handleChange}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                name="postal_code"
                label="Postal Code"
                value={formData.postal_code}
                onChange={handleChange}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Country</InputLabel>
                <Select
                  name="country"
                  value={formData.country}
                  onChange={handleChange}
                  label="Country"
                >
                  {countries.map((country) => (
                    <MenuItem key={country.code} value={country.name}>
                      {country.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                name="phone_number"
                label="Phone Number"
                value={formData.phone_number}
                onChange={handleChange}
                required
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                name="email"
                label="Email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                required
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="date"
                name="date_of_birth"
                label="Date of Birth"
                value={formData.date_of_birth}
                onChange={handleChange}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControl component="fieldset">
                <Typography variant="subtitle2" gutterBottom>
                  Gender
                </Typography>
                <RadioGroup
                  name="gender"
                  value={formData.gender}
                  onChange={handleChange}
                  row
                >
                  <FormControlLabel value="male" control={<Radio />} label="Male" />
                  <FormControlLabel value="female" control={<Radio />} label="Female" />
                </RadioGroup>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Marital Status</InputLabel>
                <Select
                  name="marital_status"
                  value={formData.marital_status}
                  onChange={handleChange}
                  label="Marital Status"
                >
                  <MenuItem value="single">Single</MenuItem>
                  <MenuItem value="married">Married</MenuItem>
                  <MenuItem value="divorced">Divorced</MenuItem>
                  <MenuItem value="widowed">Widowed</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                name="occupation"
                label="Occupation"
                value={formData.occupation}
                onChange={handleChange}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                name="reason_for_enrollment"
                label="Reason for Enrollment"
                multiline
                rows={3}
                value={formData.reason_for_enrollment}
                onChange={handleChange}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                name="expectations"
                label="What are your expectations from Foundation School?"
                multiline
                rows={3}
                value={formData.expectations}
                onChange={handleChange}
              />
            </Grid>

            <Grid item xs={12}>
              <FormControl component="fieldset">
                <Typography variant="subtitle1" gutterBottom>
                  Have you been baptized in the Holy Ghost?
                </Typography>
                <RadioGroup
                  name="baptized_in_holy_ghost"
                  value={formData.baptized_in_holy_ghost}
                  onChange={handleChange}
                  row
                >
                  <FormControlLabel value="yes" control={<Radio />} label="Yes" />
                  <FormControlLabel value="no" control={<Radio />} label="No" />
                </RadioGroup>
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <FormControl component="fieldset">
                <Typography variant="subtitle1" gutterBottom>
                  Which class did you take?
                </Typography>
                <RadioGroup
                  name="class_taken"
                  value={formData.class_taken}
                  onChange={handleChange}
                  row
                >
                  <FormControlLabel value="class_1" control={<Radio />} label="Class 1" />
                  <FormControlLabel value="class_2" control={<Radio />} label="Class 2" />
                </RadioGroup>
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                name="feedback"
                label="Do you have feedback on impact of the teaching or any areas of improvement?"
                multiline
                rows={4}
                value={formData.feedback}
                onChange={handleChange}
              />
            </Grid>

            <Grid item xs={12}>
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  size="large"
                  disabled={isCheckingDuplicate}
                >
                  {isCheckingDuplicate ? 'Checking...' : 'Submit Enrollment'}
                </Button>
              </Box>
            </Grid>
          </Grid>
        </form>

        <Snackbar
          open={showSuccess}
          autoHideDuration={2000}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        >
          <Alert severity="success">
            Enrollment submitted successfully! Redirecting...
          </Alert>
        </Snackbar>

        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}

        {duplicateInfo && (
          <Alert severity="warning" sx={{ mt: 2 }}>
            <Typography variant="body1" sx={{ fontWeight: 'bold', mb: 1 }}>
              Duplicate Enrollment Detected
            </Typography>
            <Typography variant="body2">
              An enrollment already exists for <strong>{duplicateInfo.name}</strong> on <strong>{duplicateInfo.date}</strong>.
            </Typography>
            {duplicateInfo.email && (
              <Typography variant="body2" sx={{ mt: 0.5 }}>
                Email: {duplicateInfo.email}
              </Typography>
            )}
            {duplicateInfo.phone && (
              <Typography variant="body2" sx={{ mt: 0.5 }}>
                Phone: {duplicateInfo.phone}
              </Typography>
            )}
            <Typography variant="body2" sx={{ mt: 1, fontStyle: 'italic' }}>
              If this is a different person, please contact the church office for assistance.
            </Typography>
          </Alert>
        )}

        {isCheckingDuplicate && (
          <Alert severity="info" sx={{ mt: 2 }}>
            Checking for existing enrollments...
          </Alert>
        )}
      </Paper>
    </Container>
  );
};

export default FoundationSchoolForm;

